import { State, AgentState } from '@/utils/agent/state';
import { Status, AgentStatus } from '@/utils/agent/status';

export enum CustomStatus {
    Available = 'available',
    Unavailable = 'unavailable',
    Break = 'break',
    Busy = 'busy',
    Agitated = 'agitated',
    Offline = 'offline',
}

export interface AgentConduct {
    connected: boolean;
    active: boolean;
}

export class AgentCustomStatus {
    private customStatus: CustomStatus;

    private agentConduct: AgentConduct;

    private statusFromEvent: Status;

    constructor(customStatus: CustomStatus, agentConduct: AgentConduct, statusFromEvent: Status = Status.None) {
        this.customStatus = customStatus;
        this.agentConduct = agentConduct;
        this.statusFromEvent = statusFromEvent;
    }

    static fromAgent(status: Status, state: State, previousState: State): AgentCustomStatus {
        return this.fromPanel(status, state, { connected: false, active: false }, previousState);
    }

    static fromPanel(
        status: Status,
        state: State,
        agentConduct: AgentConduct,
        previousState: State,
    ): AgentCustomStatus {
        if (AgentState.isInQueueCall(state, previousState)) {
            return new AgentCustomStatus(CustomStatus.Busy, agentConduct, status);
        }
        if (AgentState.isReceiving(state)) {
            return new AgentCustomStatus(CustomStatus.Agitated, agentConduct, status);
        }
        if (AgentStatus.isOnBreak(status)) {
            return new AgentCustomStatus(CustomStatus.Break, agentConduct, status);
        }
        if (AgentStatus.isAvailable(status) || AgentStatus.isAvailableOnDemand(status)) {
            return new AgentCustomStatus(CustomStatus.Available, agentConduct, status);
        }
        return new AgentCustomStatus(CustomStatus.Unavailable, agentConduct, status);
    }

    statusClass(): string {
        if (!this.agentConduct.connected) return 'text-gray-900 bg-gray-200';

        switch (this.customStatus) {
        case CustomStatus.Busy:
            return 'text-red-800 bg-red-100';
        case CustomStatus.Available:
            if (this.agentConduct.active) return 'text-red-800 bg-red-100';
            if (this.statusFromEvent === Status.AvailableOnDemand) return 'text-orange-800 bg-orange-100';
            return 'text-green-800 bg-green-100';
        case CustomStatus.Agitated:
            return 'text-yellow-800 bg-yellow-100';
        case CustomStatus.Break:
        case CustomStatus.Unavailable:
            return 'text-blue-500 bg-blue-100';
        default:
            if (this.agentConduct.active) return 'text-red-800 bg-red-100';
            return 'text-gray-900 bg-gray-100';
        }
    }

    statusIconClass(): string {
        if (!this.agentConduct.connected) return 'text-gray-500';

        switch (this.customStatus) {
        case CustomStatus.Busy:
            return 'text-red-500';
        case CustomStatus.Available:
            if (this.agentConduct.active) return 'text-red-500';
            if (this.statusFromEvent === Status.AvailableOnDemand) return 'text-orange-500';
            return 'text-green-500';
        case CustomStatus.Agitated:
            return 'text-yellow-500';
        case CustomStatus.Break:
        case CustomStatus.Unavailable:
            return 'text-blue-500';
        default:
            if (this.agentConduct.active) return 'text-red-500';
            return 'text-gray-500';
        }
    }

    status(): CustomStatus {
        return this.customStatus;
    }

    translation(): string {
        if (!this.agentConduct.connected) {
            return 'agent.custom_status.offline';
        }

        if (this.agentConduct.active && this.status() !== CustomStatus.Agitated) {
            return 'agent.custom_status.busy';
        }

        switch (this.status()) {
        case CustomStatus.Available:
            if (this.statusFromEvent === Status.AvailableOnDemand) {
                return 'agent.status.available_on_demand';
            }
            return 'agent.status.available';
        case CustomStatus.Busy:
            return 'agent.custom_status.busy';
        default:
            return `agent.custom_status.${this.status()}`;
        }
    }
}
